import React, { createContext, useContext, useEffect, useState } from 'react';
import { ContentfulFilter } from '@jbhi-fi/components/src/hooks/useContentfulEntries/useContentfulEntries';
import { ContentfulEntry } from '@jbhi-fi/apis/src/contentful/types';
import { CardGridCardProps } from '@jbhi-fi/components/src/ContentCard/CardGrid/CardGrid';
import { CardTransformer } from '@jbhi-fi/components/src/Contentful/transformers/CardTransformer';
import { usePublicApiClient } from '@jbhi-fi/components/src/hooks/usePublicApiClient';

const parseEntries = ({
  totalVisibleCards,
  results,
}: {
  results: Array<ContentfulEntry<any>>;
  totalVisibleCards: number;
}): Array<CardGridCardProps> => {
  return results
    .sort(
      (a, b) =>
        new Date(b.fields?.publishedDate ?? b.sys.createdAt ?? 0).getTime() -
        new Date(a.fields?.publishedDate ?? a.sys.createdAt ?? 0).getTime()
    )
    .slice(0, totalVisibleCards)
    .map(CardTransformer.toCardProps);
};

export interface UseCards {
  cards: Array<CardGridCardProps>;
  totalCards: number;
  isLoading?: boolean;
  loadMoreCards: () => void;
}

export const CardContext = createContext<UseCards>({ cards: [], totalCards: 0, loadMoreCards: () => null });

export interface CardProviderProps {
  children: React.ReactNode | React.ReactNode[];
  numCards?: number;
  useLoadingSkeletons?: boolean;
  filters: Array<ContentfulFilter>;
}

export const useCards = (): UseCards => {
  return useContext(CardContext);
};

export const CardProvider: React.FC<CardProviderProps> = ({
  children,
  numCards = 0,
  useLoadingSkeletons = false,
  filters,
}) => {
  const [page, setPage] = useState(1);
  const [cards, setCards] = useState<Array<CardGridCardProps>>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [totalResults, setTotalResults] = useState<number>(0);
  const [results, setResults] = useState<Array<ContentfulEntry<any>>>([]);

  const { contentfulClient, contentfulThemeApiClient } = usePublicApiClient();

  const isPreview = window.Jbhifi?.isContentPreview || false;
  const shouldUseThemeApi = isPreview || !contentfulClient;
  const client = shouldUseThemeApi ? contentfulThemeApiClient : contentfulClient;

  if (!contentfulClient) {
    throw new Error('useContentfulEntries must be called within a PublicApiClient provider');
  }

  useEffect(() => {
    const getEntries = async () => {
      setIsLoading(true);
      try {
        const results = await Promise.all(
          filters.map(filter => {
            return client!.getEntries({
              ...filter,
              limit: numCards.toString(),
              include: '6',
              skip: ((page - 1) * numCards).toString(),
            });
          })
        );

        setResults(previousResults =>
          results.reduce((acc, result) => {
            if (result.error || result.data) {
              // generally errors here will not cause problems, but possibly helpful to log them just in case
              console.warn('CardProvider response included errors:', result.error, filters);
            }

            return result.data?.items ? acc.concat(result.data.items as unknown as ContentfulEntry<any>) : acc;
          }, previousResults)
        );
        setTotalResults(
          results.reduce((acc, result) => {
            if (!result.data) return acc;
            return acc + result.data.total;
          }, 0)
        );
        setIsLoading(false);
      } catch (e) {
        console.error('CardProvider error:', e);
      }
    };

    getEntries();
  }, [page]);

  useEffect(() => {
    if (isLoading && useLoadingSkeletons) {
      setCards(cards =>
        cards
          .concat(Array.from(Array(numCards).fill({ cover: <></>, isLoading: true })))
          .slice(0, totalResults === 0 ? numCards : totalResults)
      );
    } else {
      setCards(parseEntries({ results, totalVisibleCards: Math.min(totalResults, numCards * page) }));
    }
  }, [isLoading]);

  return (
    <CardContext.Provider
      value={{
        cards,
        totalCards: totalResults,
        isLoading,
        loadMoreCards: () => !isLoading && setPage(page => page + 1),
      }}
    >
      {children}
    </CardContext.Provider>
  );
};
